import React from 'react';
import { Alert, Box, Typography } from '@mui/material';

export interface SwaggerRepairInfo {
  emptyRefCount: number;
  placeholderSchemas: string[];
}

export function hasSwaggerRepair(info: SwaggerRepairInfo | null | undefined): info is SwaggerRepairInfo {
  if (!info) return false;
  return info.emptyRefCount > 0 || info.placeholderSchemas.length > 0;
}

interface SwaggerRepairAlertProps {
  repair: SwaggerRepairInfo | null;
  onClose?: () => void;
}

export default function SwaggerRepairAlert({ repair, onClose }: SwaggerRepairAlertProps) {
  if (!hasSwaggerRepair(repair)) return null;

  return (
    <Alert severity="warning" onClose={onClose} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        OpenAPI file was stored after automatic repairs
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        The portal accepted your upload, but the export has issues. Try-it and model docs may be
        incomplete until you fix the source spec in the API project.
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {repair.emptyRefCount > 0 && (
          <Typography component="li" variant="body2">
            Repaired <strong>{repair.emptyRefCount}</strong> empty{' '}
            <code>$ref: &quot;#/components/schemas/&quot;</code> — replaced with generic{' '}
            <code>object</code> schemas.
          </Typography>
        )}
        {repair.placeholderSchemas.length > 0 && (
          <Typography component="li" variant="body2">
            Added <strong>{repair.placeholderSchemas.length}</strong> placeholder schema(s) for
            missing definitions:{' '}
            <code>{repair.placeholderSchemas.join(', ')}</code>
          </Typography>
        )}
      </Box>
    </Alert>
  );
}

export function formatSwaggerUploadSuccess(
  base: string,
  stats?: { pathCount?: number; bytes?: number }
): string {
  if (stats?.pathCount) {
    return `${base} (stored ${stats.pathCount} paths, ${Math.round((stats.bytes || 0) / 1024)} KB)`;
  }
  return base;
}
